import { Table } from "rsuite";
import { useCallback, useMemo, memo, useState } from "react";
import { useEditableRow, EditableRowActions, EditableTableCell, IconButton, Modal } from "../../../../components";
import { IoCloseCircle, IoCheckmarkCircle } from "react-icons/io5";
import TrashIcon from "@rsuite/icons/Trash";
import { useAuthenticationService } from "../../../../services/authentication.service";
import { useBankFileService } from "../../contexts/BankFileServiceContext";
import { useTransactionsService } from "./context/transactions.context";
import { roles } from "../../../../utils/constants";
import ToastService from "../../../../services/toast.service";
import logger from "../../../../utils/logger";
import { ro } from "date-fns/locale";

const { Column, HeaderCell, Cell } = Table;

// Field configuration for editable MCOT transaction fields
const FIELD_CONFIG = {
  date: { type: "date" },
  amount: { type: "number", step: "0.01" },
  particulars: { type: "text" },
  code: { type: "text" },
  reference: { type: "text" },
  otherParty: { type: "text" },
};

function McotTransactionsTable({ 
  bankFile, 
  fileTypes, 
  data, 
  sortColumn, 
  sortType, 
  onSortColumn,
}) {
  const transactionsService = useTransactionsService();
  const authService = useAuthenticationService();
  const bankFileService = useBankFileService();
  const isAdmin = useMemo(() => authService.isInAnyRole(roles.ADMINS), [authService]);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle saving MCOT transaction edits
  const handleSaveTransaction = useCallback(
    async (id, updatedData) => {
      if (!id || !updatedData) throw new Error("Invalid data for saving transaction");
      const existingTransaction = data.find((transaction) => transaction.id === id);
      if (!existingTransaction) throw new Error("Transaction not found for saving");
      
      const updatedTransaction = { ...existingTransaction, ...updatedData };
      await transactionsService.updateTransactionMcot(updatedTransaction);
      ToastService.success("Transaction updated successfully");
    },
    [data, transactionsService]
  );

  // Delete handlers (following BankFileTable pattern)
  const handleDeleteClick = useCallback((transaction) => {
    setTransactionToDelete(transaction);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setTransactionToDelete(null);
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setIsDeleting(true);
    
    if (!transactionToDelete) {
      logger.error("No transaction to delete");
      ToastService.error("No transaction selected for deletion");
      setIsDeleting(false);
      return;
    }

    transactionsService
      .deleteTransaction(transactionToDelete.id, bankFile?.bankFileId)
      .then(() => {
        logger.info("Transaction deleted successfully");
        ToastService.success("Transaction deleted successfully");
      })
      .catch((error) => {
        logger.error("Error deleting transaction:", error);
        ToastService.error(`Failed to delete transaction: ${error.message || "Unknown error"}`);
      })
      .finally(() => {
        setDeleteModalOpen(false);
        setIsDeleting(false);
        setTransactionToDelete(null);
      });
  }, [transactionToDelete, transactionsService, bankFile?.bankFileId]);

  // Editable row functionality
  const { 
    editingRowId, 
    editingData, 
    saving, 
    startEditing, 
    cancelEditing, 
    handleFieldChange, 
    saveRow, 
    isEditing 
  } = useEditableRow({
    onSave: handleSaveTransaction,
    onError: (error) => {
      logger.error("Error saving transaction:", error);
      ToastService.error(error.response?.data || "Unknown error");
    },
  });

  // Stable amount formatter function
  const formatAmount = useCallback((rowData) => {
    const isEditingRow = editingRowId === rowData.id;
    if (isEditingRow) {
      return (
        <EditableTableCell
          rowData={rowData}
          fieldKey="amount"
          fieldConfig={FIELD_CONFIG.amount}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      );
    }
    const amt = Number(rowData.amount);
    if (!Number.isFinite(amt)) return rowData.amount ?? "";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amt);
  }, [editingRowId, editingData, handleFieldChange]);

  const exportAs = useCallback(
    (rowData) => {
      if(!fileTypes || fileTypes.length === 0) return "N/A";
      const id = rowData?.exportTypeId || bankFile?.fileTypeId;
      return fileTypes?.find((ft) => ft.fileTypeId === id)?.code || "N/A";
    },
    [bankFile, fileTypes]
  );

  // Stable export status renderer function (same as BankTransactionsTable)
  const renderExportStatus = useCallback(
    (rowData) => {
      return (
        <div className="export-status">
          {rowData?.ignore ? (
            <div>{bankFileService.getIgnoreReasonById(rowData?.ignoreReasonId)?.reason}</div>
          ) : rowData.exportBatchId ? (
            <IoCheckmarkCircle className="exported-icon" />
          ) : (
            <IoCloseCircle className="not-exported-icon" />
          )}
        </div>
      );
    },
    [bankFileService]
  );

  // Render child ID indicator (check mark or X)
  const renderChildIndicator = useCallback(
    (rowData) => {
      return (
        <div className="export-status">
          {rowData?.childId ? (
            <IoCheckmarkCircle className="exported-icon" />
          ) : (
            <IoCloseCircle className="not-exported-icon" />
          )}
        </div>
      );
    },
    []
  );

  // Stable actions column renderer function
  const renderActionsColumn = useCallback(
    (rowData) => (
     
      <EditableRowActions
        disabled={rowData?.exportBatchId !== null}
        rowData={rowData}
        isEditing={isEditing(rowData.id)}
        onEdit={startEditing}
        onSave={saveRow}
        onCancel={cancelEditing}
        saving={saving}
      />
    ),
    [isEditing, startEditing, saveRow, cancelEditing, saving]
  );

  // Delete column renderer function (following BankFileTable pattern)
  const renderDeleteColumn = useCallback(
    (rowData) => (
      <div>
        <IconButton
          icon={<TrashIcon />}
          size="xs"
          width={30}
          height={30}
          appearance="subtle"
          onClick={() => handleDeleteClick(rowData)}
          title="Delete Transaction"
          className="delete-button"
          disabled={isDeleting}
          data-testid={`delete-transaction-${rowData.id}`}
        />
      </div>
    ),
    [handleDeleteClick, isDeleting]
  );

  // Render editable cell or display value
  const renderEditableCell = useCallback(
    (fieldKey, fieldConfig) => (rowData) => {
      const isEditingRow = editingRowId === rowData.id;
      if (isEditingRow) {
        return (
          <EditableTableCell
            rowData={rowData}
            fieldKey={fieldKey}
            fieldConfig={fieldConfig}
            editingRowId={editingRowId}
            editingData={editingData}
            onFieldChange={handleFieldChange}
          />
        );
      }
      return <span>{rowData[fieldKey] || ""}</span>;
    },
    [editingRowId, editingData, handleFieldChange]
  );

  // Memoized columns configuration
  const columns = useMemo(() => {
    const baseColumns = [
      <Column key="date" width={150} sortable resizable>
        <HeaderCell>Date</HeaderCell>
        <Cell dataKey="date">{renderEditableCell("date", FIELD_CONFIG.date)}</Cell>
      </Column>,
      <Column key="amount" width={100} sortable resizable>
        <HeaderCell>Amount</HeaderCell>
        <Cell dataKey="amount">{formatAmount}</Cell>
      </Column>,
      <Column key="particulars" width={200} sortable resizable>
        <HeaderCell>Particulars</HeaderCell>
        <Cell dataKey="particulars">{renderEditableCell("particulars", FIELD_CONFIG.particulars)}</Cell>
      </Column>,
      <Column key="code" width={120} sortable resizable>
        <HeaderCell>Code</HeaderCell>
        <Cell dataKey="code">{renderEditableCell("code", FIELD_CONFIG.code)}</Cell>
      </Column>,
      <Column key="reference" width={150} sortable resizable>
        <HeaderCell>Reference</HeaderCell>
        <Cell dataKey="reference">{renderEditableCell("reference", FIELD_CONFIG.reference)}</Cell>
      </Column>,
      <Column key="otherParty" flexGrow={2} width={228} sortable resizable>
        <HeaderCell>Other Party</HeaderCell>
        <Cell dataKey="otherParty">{renderEditableCell("otherParty", FIELD_CONFIG.otherParty)}</Cell>
      </Column>,
      <Column key="type" width={80} sortable>
        <HeaderCell>Type</HeaderCell>
        <Cell dataKey="type" />
      </Column>,
      <Column key="ExportType" width={100}>
        <HeaderCell>Export Type</HeaderCell>
        <Cell dataKey="exportType">{exportAs}</Cell>
      </Column>,
      <Column key="exported" width={100} sortable>
        <HeaderCell>Exported</HeaderCell>
        <Cell dataKey="ignore">{renderExportStatus}</Cell>
      </Column>,
      <Column key="childIndicator" width={100} sortable>
        <HeaderCell>Child Linked</HeaderCell>
        <Cell dataKey="childId">{renderChildIndicator}</Cell>
      </Column>,
    ];

    if (isAdmin) {
      return [
        <Column key="actions" width={100} sortable>
          <HeaderCell>Actions</HeaderCell>
          <Cell dataKey="id">{renderActionsColumn}</Cell>
        </Column>,
        <Column key="childIndicator" width={100} sortable>
          <HeaderCell>Child Linked</HeaderCell>
          <Cell dataKey="childId">{renderChildIndicator}</Cell>
        </Column>,
        ...baseColumns.filter(col => col.key !== 'childIndicator'), // Remove from base columns to avoid duplication
         <Column key="delete" width={80} sortable={false}>
          <HeaderCell>Delete</HeaderCell>
          <Cell dataKey="id">{renderDeleteColumn}</Cell>
        </Column>
      ];
    }

    return baseColumns;
  }, [isAdmin, formatAmount, renderEditableCell, renderChildIndicator, renderExportStatus, renderActionsColumn, renderDeleteColumn, exportAs]);

  // Stable row className function
  const getRowClassName = useCallback((rowData) => {
    const baseClass = "expanded-row-content";
    return rowData?.ignore ? `${baseClass} ignored-transaction` : baseClass;
  }, []);

  return (
    <>
      <Table
        rowKey="id"
        data={data}
        height={320}
        bordered
        cellBordered
        style={{ width: "100%" }}
        rowClassName={getRowClassName}
        className="bank-transactions-table"
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
      >
        {columns}
      </Table>

      {/* Delete Confirmation Modal (following BankFileTable pattern) */}
      <Modal
        open={deleteModalOpen}
        title="Delete Transaction"
        text={`Are you sure you want to delete this transaction? This action cannot be undone.`}
        onClose={closeDeleteModal}
        confirmText="DELETE TRANSACTION"
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        confirmDisabled={isDeleting}
        cancelDisabled={isDeleting}
        confirmTestId="confirm-delete-transaction"
        cancelTestId="cancel-delete-transaction"
      />
    </>
  );
}

export default memo(McotTransactionsTable);